"use client";

import { useState } from "react";
import {
  HiSparkles,
  HiQuestionMarkCircle,
  HiClipboardCopy,
  HiCheckCircle,
  HiRefresh,
} from "@/components/icons";
import ToolsCustomDevCTA from "@/components/ToolsCustomDevCTA";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import ToolPageSchema from "@/components/schemas/ToolPageSchema";
import UnifiedFAQ from "@/components/UnifiedFAQ";

interface PromptFields {
  role: string;
  context: string;
  task: string;
  format: string;
  constraints: string;
}

interface Preset {
  name: string;
  description: string;
  fields: PromptFields;
}

const PRESETS: Preset[] = [
  {
    name: "Blog Writer",
    description: "Write engaging, SEO-friendly blog posts",
    fields: {
      role: "an expert content writer with 10 years of experience writing high-converting blog posts for SaaS companies",
      context:
        "I run a B2B SaaS blog targeting marketing professionals. The audience is knowledgeable and values actionable advice over theory.",
      task: "Write a 1500-word blog post on the topic I provide. Include a compelling headline, an intro that hooks the reader, 3-5 sections with subheadings, practical examples, and a clear conclusion with a call to action.",
      format:
        "Markdown format with H2 and H3 headings, bullet points where appropriate, and a meta description at the end.",
      constraints:
        "Do not use jargon without explaining it. Avoid passive voice. Do not use filler phrases like 'In today's fast-paced world'. Keep sentences under 25 words on average.",
    },
  },
  {
    name: "Code Reviewer",
    description: "Review code for bugs, style, and performance",
    fields: {
      role: "a senior software engineer with expertise in clean code principles, performance optimization, and security best practices",
      context:
        "I am working on a production application and need a thorough code review before merging a pull request.",
      task: "Review the code I provide. Identify bugs, security vulnerabilities, performance issues, and style violations. Suggest concrete improvements with code examples where helpful.",
      format:
        "Structured review with sections: 1) Critical Issues, 2) Warnings, 3) Suggestions, 4) Positive Notes. Use code blocks for examples.",
      constraints:
        "Be direct and specific — avoid vague feedback like 'this could be improved'. If you suggest a change, show the improved code. Do not rewrite the entire function unless necessary.",
    },
  },
  {
    name: "Data Analyst",
    description: "Analyze data and extract insights",
    fields: {
      role: "a senior data analyst with expertise in statistical analysis, data visualization, and business intelligence",
      context:
        "I need to analyze a dataset and present findings to non-technical stakeholders who need actionable insights, not raw numbers.",
      task: "Analyze the data I provide. Identify key trends, anomalies, and actionable insights. Suggest follow-up questions and potential visualizations that would best communicate the findings.",
      format:
        "Executive summary first (3-5 bullet points), then detailed findings with supporting data, and finally recommendations. Use plain language throughout.",
      constraints:
        "Do not assume statistical knowledge in the reader. Explain any technical terms in plain English. Focus on business impact, not just statistical significance.",
    },
  },
  {
    name: "Marketing Strategist",
    description: "Create marketing strategies and copy",
    fields: {
      role: "a growth marketing strategist with experience scaling SaaS products from 0 to $10M ARR using data-driven campaigns",
      context:
        "I am launching a new product and need a marketing strategy. My target audience is [describe audience] and my budget is [describe budget].",
      task: "Create a 90-day marketing strategy including: positioning statement, key messaging for different audience segments, channel recommendations with rationale, and KPIs to track success.",
      format:
        "Structured strategy document with sections for positioning, messaging, channels, timeline, and KPIs. Include specific, measurable goals.",
      constraints:
        "Focus on channels and tactics that can realistically be executed with a small team. Prioritize high-ROI activities. Do not recommend tactics that require a large agency budget.",
    },
  },
];

function assemblePrompt(fields: PromptFields): string {
  const parts: string[] = [];
  if (fields.role.trim()) {
    parts.push(`Act as ${fields.role}.`);
  }
  if (fields.context.trim()) {
    parts.push(`Context: ${fields.context}`);
  }
  if (fields.task.trim()) {
    parts.push(`Task: ${fields.task}`);
  }
  if (fields.format.trim()) {
    parts.push(`Output format: ${fields.format}`);
  }
  if (fields.constraints.trim()) {
    parts.push(`Constraints: ${fields.constraints}`);
  }
  return parts.join("\n\n");
}

const EMPTY_FIELDS: PromptFields = {
  role: "",
  context: "",
  task: "",
  format: "",
  constraints: "",
};

const faqItems = [
  {
    question: "What is prompt engineering?",
    answer:
      "Prompt engineering is the practice of crafting AI inputs (prompts) to get the most useful, accurate, and relevant outputs from large language models like ChatGPT, Claude, or Gemini. A well-structured prompt provides the AI with role context, background information, a clear task, output format requirements, and any constraints — leading to dramatically better results than a simple question.",
  },
  {
    question: "What does each field do?",
    answer:
      '"Role" sets the persona and expertise level for the AI. "Context" provides background information the AI needs to understand your situation. "Task" is the specific thing you want the AI to do. "Format" specifies how you want the output structured. "Constraints" are rules or limitations the AI should follow. Together these five elements produce a highly structured, effective prompt.',
  },
  {
    question: "Which AI tools can I use these prompts with?",
    answer:
      "The prompts generated by this tool work with any major AI assistant including ChatGPT (GPT-4o), Claude (Anthropic), Gemini (Google), Copilot (Microsoft), and Perplexity. The structured format is model-agnostic — paste it directly into any chat interface.",
  },
  {
    question: "How do I use the template presets?",
    answer:
      "Click any preset button (Blog Writer, Code Reviewer, etc.) to load a professionally written template into all five fields. You can then customize any field to match your specific needs. Presets are great starting points — treat them as templates to edit, not prompts to use verbatim.",
  },
  {
    question: "What makes a good prompt?",
    answer:
      "The best prompts are specific, not vague. Instead of 'write a blog post', say 'write a 1500-word blog post targeting B2B SaaS marketing managers on the topic of email automation'. Provide enough context for the AI to understand your situation, specify exactly how you want the output structured, and add constraints to prevent common mistakes.",
  },
];

export default function AiPromptBuilderClient() {
  const [fields, setFields] = useState<PromptFields>(EMPTY_FIELDS);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const assembled = assemblePrompt(fields);

  const update = (key: keyof PromptFields) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setActivePreset(null);
    setFields((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const applyPreset = (preset: Preset) => {
    setFields(preset.fields);
    setActivePreset(preset.name);
  };

  const reset = () => {
    setFields(EMPTY_FIELDS);
    setActivePreset(null);
    setCopied(false);
  };

  const copyPrompt = () => {
    navigator.clipboard.writeText(assembled).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="container px-4 py-12 mx-auto max-w-5xl">
      <ToolPageSchema
        name="AI Prompt Builder"
        description="Build structured AI prompts with Role, Context, Task, Format, and Constraints fields. Live preview with preset templates for ChatGPT, Claude, and more."
        url="https://plugmonkey.xyz/tool/ai-prompt-builder/"
        breadcrumbs={[
          { name: "Home", url: "https://plugmonkey.xyz/" },
          { name: "Free Tools", url: "https://plugmonkey.xyz/tools/" },
          {
            name: "AI Prompt Builder",
            url: "https://plugmonkey.xyz/tool/ai-prompt-builder/",
          },
        ]}
      />

      <div className="flex items-center gap-4 mb-8 mt-6">
        <div className="p-3 bg-primary/10 rounded-full text-primary">
          <HiSparkles className="w-8 h-8" />
        </div>
        <div>
          <h1 className="text-3xl md:text-4xl font-bold">AI Prompt Builder</h1>
          <p className="text-muted-foreground mt-1">
            Build structured prompts for ChatGPT, Claude, and Gemini. Fill in the fields — the
            prompt assembles itself.
          </p>
        </div>
      </div>

      <div className="mb-8 text-muted-foreground leading-relaxed space-y-3 max-w-3xl">
        <p>
          Generic questions get generic answers. The difference between a mediocre AI response and a
          genuinely useful one usually comes down to how the prompt is structured. This tool guides
          you through the five key components of an effective prompt: Role, Context, Task, Format,
          and Constraints.
        </p>
        <p>
          Fill in any or all fields and the prompt assembles in real time on the right. Use one of
          the preset templates as a starting point, or build your own from scratch. Copy the final
          prompt and paste it into any AI chat interface.
        </p>
      </div>

      {/* Preset Buttons */}
      <div className="mb-8">
        <p className="text-sm font-semibold mb-3 text-muted-foreground uppercase tracking-wide">
          Template Presets
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => applyPreset(preset)}
              className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                activePreset === preset.name
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-background border-border hover:border-primary/60 hover:bg-primary/5"
              }`}
              title={preset.description}
            >
              {preset.name}
            </button>
          ))}
          <button
            onClick={reset}
            className="px-4 py-2 rounded-full text-sm font-medium border border-border hover:border-red-400 hover:text-red-500 transition-colors"
          >
            <HiRefresh className="inline w-3 h-3 mr-1" />
            Clear
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="apb-role" className="font-semibold">
              Role <span className="text-muted-foreground font-normal text-xs">(Act as a...)</span>
            </Label>
            <Textarea
              id="apb-role"
              value={fields.role}
              onChange={update("role")}
              placeholder="e.g. an expert content writer with 10 years of experience..."
              className="h-24 resize-none text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="apb-context" className="font-semibold">
              Context{" "}
              <span className="text-muted-foreground font-normal text-xs">(Given that...)</span>
            </Label>
            <Textarea
              id="apb-context"
              value={fields.context}
              onChange={update("context")}
              placeholder="e.g. I run a B2B SaaS blog targeting marketing professionals..."
              className="h-28 resize-none text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="apb-task" className="font-semibold">
              Task <span className="text-muted-foreground font-normal text-xs">(Please...)</span>
            </Label>
            <Textarea
              id="apb-task"
              value={fields.task}
              onChange={update("task")}
              placeholder="e.g. Write a 1500-word blog post. Include an intro, 3-5 sections, and a conclusion with a CTA."
              className="h-28 resize-none text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="apb-format" className="font-semibold">
              Format{" "}
              <span className="text-muted-foreground font-normal text-xs">(Output as...)</span>
            </Label>
            <Textarea
              id="apb-format"
              value={fields.format}
              onChange={update("format")}
              placeholder="e.g. Markdown with H2/H3 headings, bullet points, and a meta description at the end."
              className="h-24 resize-none text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="apb-constraints" className="font-semibold">
              Constraints{" "}
              <span className="text-muted-foreground font-normal text-xs">(Do not...)</span>
            </Label>
            <Textarea
              id="apb-constraints"
              value={fields.constraints}
              onChange={update("constraints")}
              placeholder="e.g. Do not use jargon without explaining it. Avoid passive voice. Keep sentences under 25 words."
              className="h-24 resize-none text-sm"
            />
          </div>
        </div>

        {/* Live Preview */}
        <div className="lg:sticky lg:top-6 h-fit">
          <Card className="bg-slate-950 text-slate-50 border-slate-800 shadow-xl overflow-hidden">
            <CardHeader className="bg-slate-900 border-b border-slate-800 flex flex-row items-center justify-between py-3">
              <div>
                <CardTitle className="text-slate-50 font-mono text-sm flex items-center gap-2">
                  <HiSparkles className="text-primary" /> Your Prompt
                </CardTitle>
                <CardDescription className="text-slate-400 text-xs mt-0.5">
                  {assembled.length} characters
                </CardDescription>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={copyPrompt}
                disabled={!assembled}
                className="text-slate-400 hover:text-white hover:bg-slate-800"
              >
                {copied ? (
                  <HiCheckCircle className="w-4 h-4 mr-1 text-green-400" />
                ) : (
                  <HiClipboardCopy className="w-4 h-4 mr-1" />
                )}
                {copied ? "Copied!" : "Copy"}
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <pre className="p-5 text-sm leading-relaxed whitespace-pre-wrap font-sans text-slate-200 min-h-[300px] max-h-[600px] overflow-y-auto">
                {assembled || "Start filling in the fields on the left to build your prompt..."}
              </pre>
            </CardContent>
            {assembled && (
              <div className="p-4 bg-slate-900 border-t border-slate-800">
                <Button
                  onClick={copyPrompt}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
                >
                  {copied ? (
                    <HiCheckCircle className="w-4 h-4 mr-2" />
                  ) : (
                    <HiClipboardCopy className="w-4 h-4 mr-2" />
                  )}
                  {copied ? "Copied to clipboard!" : "Copy Prompt"}
                </Button>
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* FAQ Section */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border bg-card p-8 md:p-12 shadow-sm">
            <div className="flex items-center gap-4 mb-10">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <HiQuestionMarkCircle className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                <p className="text-muted-foreground text-sm">
                  Click on a question to view the answer.
                </p>
              </div>
            </div>
            <UnifiedFAQ items={faqItems} showSupportLink={false} className="space-y-4" />
          </div>
        </div>
      </section>

      {/* Prompt Library CTA */}
      <section className="mt-16 pt-12 border-t border-border">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-border/50 bg-foreground text-background p-8 md:p-12 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary/10 rounded-full blur-[80px] pointer-events-none" />
            <div className="relative z-10 text-center">
              <h2 className="text-2xl md:text-3xl font-bold tracking-tighter mb-4">
                Need Prompt Inspiration?
              </h2>
              <p className="text-background/60 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
                Browse 350+ copy-paste prompts for marketing, coding, writing, sales, and more —
                ready to use with ChatGPT, Claude, Gemini, or any AI.
              </p>
              <a
                href="/prompts/"
                className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-primary text-primary-foreground font-bold text-base hover:bg-primary/90 transition-all hover:scale-105 shadow-lg shadow-primary/20"
              >
                Browse Prompt Library <HiSparkles className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ToolsCustomDevCTA />
    </div>
  );
}
